import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import useForm from '@utils/useForm';
import { Helpers } from '@utils/helpers';
import { GAAviva } from '@utils/gaAviva';

import { useTheme, ITheme } from 'src/theme';
import { useAvivaSelector, useAvivaDispatch } from '@redux/hook';
import { isEmpty, get, find, set } from 'lodash';
import { CoverageForm } from '@models/DPI/Coverage.model';
import { SQSPocketRequest, LifeProfile, Coverages, SQSPocketResponse } from '@models/SQSPocketRequest.model';
import { updateSQSQuotation, updateCoverage, updateQuoteResponse, completeStep, updateQuoteRequest, updateTransactionId } from '@redux/slices/DPI/step1Slice';
import { setIsGlobalError, setLoadingState } from '@redux/slices/loadingSlice';
import { updateReadDocs, updateDeclaration, completeStep as completeStep4 } from '@redux/slices/DPI/step4Slice';

import { postComputePremium } from '@api/index';

import { LeftGrid, RightGrid, FullGrid, EmptyGrid, ContainerGrid, Container } from '@components/LayoutGrid';
import Button from '@components/Button';
import MultiSteps from '@components/MultiSteps';
import Heading from '@components/Heading';
import StickyBox from '@components/StickyBox';
import Text from '@components/Text';
import Input from '@components/Input';
import Dropdown from '@components/Dropdown';
import ContainerBlock from '@components/ContainerBlock';
import TextCollapsible from '@components/TextCollapsible';
import ContactBox from '@components/ContactBox';
import Divider from '@components/Divider';
import BulletList from '@components/BulletList';
import Bullet from '@components/Bullet';
import BeforeProcessWarning from '@components/BeforeProcessWarning';
import Quote from '@components/Quote';
import PremiumCost from '@components/PremiumCost';
import PolicyDocument from '@components/PolicyDocument';
import Modal from '@components/Modal';
import { validate } from './service';
import { fileLinkGen } from '@static/staticConstents';

const page = 'Coverage';

const quoteInitialSample = [
  {
    name: 'Basic plan',
    type: 'basic',
    details: [
      { text: 'Plan', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Policy term / Premium payment term*', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Add-ons',
    type: 'add-on',
    details: [
      { text: 'Rider', value: '-' },
      { text: 'Sum assured', value: '-' },
      { text: 'Policy term / Premium payment term*', value: '-' },
      { text: 'Premium amount', value: '-' },
    ]
  },
  {
    name: 'Other details',
    type: 'others',
    details: [
      { text: 'Premium payment frequency', value: '-' },
    ]
  },
]

const createSQSPayload = ({ basicDetails, coverage, transactionId }: any): SQSPocketRequest => {
  const { day, month, year } = basicDetails.dob;
  const stringDate = `${month}/${day}/${year}`;
  const age = Helpers.getAge(new Date(stringDate));
  const lifeProfile = new LifeProfile({
    ...basicDetails,
    dob: `${day}${month}${year}`,
    age
  });
  const { nationalityCode } = basicDetails;
  const { policyTerm, planPremiumMode, sumAssured, illnessCover } = coverage;

  const attributePlan = {
    policyTerm,
    sumAssured: Number(sumAssured),
    planPremiumMode,
    nationalityCode,
    currency: "SGD",
    residencyCode: 'SG'
  }
  const coveragePlan = new Coverages({ id: 'lifeDirect', type: 'P', attributes: attributePlan });
  let payload = new SQSPocketRequest({
    lifeprofiles: [lifeProfile],
    coverages: [coveragePlan],
    transactionId
  });

  if (!isEmpty(illnessCover)) {
    const attributeIllnessCover = {
      policyTerm,
      nationalityCode,
      planPremiumMode,
      sumAssured: Number(illnessCover),
      residencyCode: 'SG',
      currency: "SGD"
    };
    const coverageIllnessCover = new Coverages({ id: 'lifeDirect_CIAB', type: 'R', attributes: attributeIllnessCover });
    console.log(coverageIllnessCover);

    payload.coverages.push(coverageIllnessCover);
  }

  return payload;
}


const trackOnload = (premium: SQSPocketResponse | null): void => {
  const GA = GAAviva.getInstance();
  let { acquisition, form } = GA.digitalData;
  let { insurance, product } = acquisition;
  product = { ...product, prodID: 'lifeDirect' };
  form.name = 'Coverage';
  if (premium) {
    product.prodName = premium.name;
    insurance = {
      ...insurance,
      premium: `${premium.totalAnnualizedPremium}`,
      premiumTerm: premium.dpiPremiumTerm,
      sumassured: `${premium.sumAssured}`,
      addOns: premium.dpiAddon
    }
  }
  GA.trackPageLoad({ acquisition: { ...acquisition, insurance, product } });
}

const trackFormSubmmit = (error: string = ''): void => {
  const GA = GAAviva.getInstance();
  if (isEmpty(error)) {
    GA.trackFormSuccess({});
    return;
  }
  const { form } = GA.digitalData;
  form.error.messages = error;
  GA.trackFormFailed({ form });
}


export interface CoverageProps { }

function Coverage(props: CoverageProps) {
  console.log(`Aviva - ${page}`);
  const theme: ITheme = useTheme();
  const dispatch = useAvivaDispatch();
  const history = useHistory();
  const urlConfig = get(window, 'AVIVA_VARIABLE_CONFIG');
  const insuranceEstimatorUrl = get(urlConfig, 'insuranceEstimatorUrl');
  const budgetCalculatorUrl = get(urlConfig, 'budgetCalculatorUrl');;
  const compareFirstUrl = get(urlConfig, 'compareFirstUrl');;;

  const basicDetails = useAvivaSelector(state => state.dpiStep1.basicDetails);
  const initValue = useAvivaSelector(state => state.dpiStep1.coverage);
  const quoteRawResponse = useAvivaSelector(state => state.dpiStep1.quoteRawResponse);
  const selectedOccupation = useAvivaSelector(state => state.dpiStep1.occupation);
  const hasMyInfo = useAvivaSelector(state => state.dpiStep2.hasMyInfo);

  const contactInformation = useAvivaSelector(state => state.staticData.contactInformation);
  const premiumTerms = useAvivaSelector(state => state.staticData.premiumTerms);
  const paymentFrequency = useAvivaSelector(state => state.staticData.paymentFrequency);

  const transactionId= useAvivaSelector(state=>state.dpiStep1.transactionId)  
  
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isCalculated, setIsCalculated] = useState<boolean>(false);
  const [needToCalculate, setNeedToCalculate] = useState<boolean>(false);
  const [premium, setPremium] = useState<any>({});
  const [quotes, setQuotes] = useState<any>([]);
  const [isShowConfirm, setIsShowConfirm] = useState<boolean>(false);
  const [productSummary, setProductSummary] = useState<string>('');
  const [premiumTermOptions, setPremiumTermOptions] = useState<any[]>(premiumTerms);
  const [aged, setAged] = useState<number>(0);
  const [isShowCI, setShowCI] = useState<boolean>(true);

  const toggleModal = () => { setIsShowConfirm(!isShowConfirm); };

  const { handleChange, values, errors, formValid, patchValue: patchForm } = useForm(new CoverageForm(initValue), () => { }, validate);

  const onBack = useCallback(() => { history.push('/dpi/basic-details') }, [history]);

  const gotoContact = () => { history.push(`/dpi/contact`) };

  const onContinue = useCallback(() => {
    dispatch(updateCoverage(values));
    dispatch(completeStep(true));

    if (!hasMyInfo) {
      history.push('/dpi/my-info')
    } else {
      history.push('/dpi/my-info?step=1')
    }
  }, [dispatch, values, hasMyInfo, history]);


  const getProductSummary = useCallback(() => {
    const isCIRider = values['illnessCover'].length > 0;
    if (isCIRider) {
      setProductSummary(fileLinkGen("dpi","DirectsalesDPI-PS-CI.pdf"));
    } else {
      setProductSummary(fileLinkGen("dpi","DirectsalesDPI-PS.pdf"));
    }
  }, [values])

  const setCalculatedPremium = useCallback((primum: SQSPocketResponse) => {
    const quote = primum.dpiQuote;
    const premium = primum.dpiPremium;
    setQuotes(quote);
    setPremium(premium);
    setIsCalculated(true);
    setNeedToCalculate(false);
    getProductSummary();
    trackOnload(primum);
  }, [setQuotes, setPremium, setIsCalculated, getProductSummary]);


  const onCalculatePremium = useCallback(async () => {
    if (!basicDetails) {
      onBack();
      return;
    }
    dispatch(setLoadingState(true));
    let newTransactionId = transactionId;
    if (!transactionId) {
      newTransactionId = window.crypto.randomUUID();
      dispatch(updateTransactionId(newTransactionId));
    }

    const payload: SQSPocketRequest = createSQSPayload({ basicDetails, coverage: values, transactionId: newTransactionId });
    dispatch(updateQuoteRequest({ ...payload, document: ['cp', 'bi'] }));
    try {
      const response = await postComputePremium(payload);
      const data = get(response, 'data.data', {});
      const pocket = new SQSPocketResponse(data);
      setCalculatedPremium(pocket)
      pocket.isCIRider = values['illnessCover'] > 0;
      dispatch(updateCoverage(values));
      dispatch(updateSQSQuotation(pocket));
      dispatch(updateQuoteResponse(data));
      dispatch(setLoadingState(false));
      dispatch(updateDeclaration(null));
      dispatch(updateReadDocs(null));
      dispatch(completeStep4(false));
      trackFormSubmmit();
      window.scrollTo(0, 0);
    } catch (error) {
      console.error(error);
      dispatch(setIsGlobalError(true));
      trackFormSubmmit(get(error, 'message'))
      dispatch(setLoadingState(false));
    }
  }, [dispatch, basicDetails, values, onBack, setCalculatedPremium]);

  const patchValue = (value: string, type: 'planPremiumMode' | 'policyTerm', target: any) => {
    const nextValue = type === 'planPremiumMode' ? Helpers.getPremiumTermByCode(value) : Helpers.getPolicyTermByCode(value);
    const otherTarget = type === 'planPremiumMode' ? find(target, { type: 'others' }) : find(target, { type: 'basic' });
    if (otherTarget) {
      if (type === 'planPremiumMode') {
        set(otherTarget, 'details[0].value', nextValue);
      }
      else {
        set(otherTarget, 'details[2].value', nextValue);
        const addonTarget = find(target, { type: 'add-on' });
        if (get(addonTarget, 'details[0].value') !== '-') {
          console.info(get(addonTarget, 'details[0].value'));
          set(addonTarget, 'details[2].value', nextValue);
        }
      }
    }
    return [...target];
  }

  const onBlurBasicSumAssrued = () => {
    if (!isEmpty(values['sumAssured']) && errors?.sumAssured?.isValid) {
      patchForm({ illnessCover: values['sumAssured'] });

      if (isEmpty(premium)) {
        const basicTarget = find(quotes, { type: 'basic' });
        const otherTarget = find(quotes, { type: 'add-on' });
        set(otherTarget, 'details[0].value', values['sumAssured'] ? 'DIRECT - Singlife Critical Illness (Term Life) (2025) ' : '-');
        set(otherTarget, 'details[1].value', values['sumAssured'] ? Helpers.toCurrency(Number(values['sumAssured'])) : '-');
        set(otherTarget, 'details[2].value', get(basicTarget, 'details[2].value'));
        setQuotes([...quotes]);
      }
    }
  }

  const onChange = useCallback((event: any) => {
    if (!needToCalculate) {
      setNeedToCalculate(true);
    }
    handleChange(event);
    const { name, value } = event.target;
    if (name === 'sumAssured' && isEmpty(premium)) {
      const otherTarget = find(quotes, { type: 'basic' });
      set(otherTarget, 'details[0].value', value ? 'DIRECT - Singlife Term Life' : '-');
      set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');
      setQuotes([...quotes]);
    }
    if (name === 'illnessCover' && isEmpty(premium)) {
      const otherTarget = find(quotes, { type: 'add-on' });
      set(otherTarget, 'details[0].value', value ? 'DIRECT - Singlife Critical Illness (Term Life) (2025) ' : '-');
      set(otherTarget, 'details[1].value', value ? Helpers.toCurrency(Number(value)) : '-');
      setQuotes([...quotes]);
    }
  }, [errors, quotes, setQuotes]);
  const isShowAccidentRider = useCallback((selectedValue, age) => {
    if (selectedOccupation?.status === 'Decline') {
      return false;
    }
    const selected = premiumTerms.find(o => o.code === selectedValue);
    if (selected) {
      const { min, applicableForCI } = selected;
      const isShow = age >= min && age <= applicableForCI;
      console.log('isShowAccidentRider', age, selected, isShow);
      if (!isShow) {
        patchForm({ illnessCover: '' });
      }
      return isShow;
    }

    return true;
  }, [premiumTerms, patchForm, selectedOccupation])
  const onChangeDropdown = (type: 'planPremiumMode' | 'policyTerm') => (event: any) => {
    const { value } = event.target;
    if (isEmpty(premium)) {
      const nextQuotes = patchValue(value, type, quotes);
      setQuotes(nextQuotes);
    }

    if (!needToCalculate) {
      setNeedToCalculate(true);
    }
    if (type === 'policyTerm') {
      const shouldShowCI = isShowAccidentRider(value, aged);
      setShowCI(shouldShowCI);
    }
    handleChange(event);
  }


  const filterPremiumTerms = useCallback(() => {
    if (basicDetails?.dob && premiumTerms) {
      const { day, month, year } = basicDetails.dob;
      const stringDate = `${month}/${day}/${year}`;
      const refDay = new Date(stringDate);
      const aged = Helpers.getAge(refDay);
      setAged(aged);
      const isShow = isShowAccidentRider(initValue?.policyTerm, aged);
      setShowCI(isShow);
      console.log('premiumTermsListing', { aged, premiumTerms });
      return premiumTerms.filter(o => aged >= o.min && aged <= o.max);
    }
    return [];


  }, [premiumTerms, basicDetails, initValue, isShowAccidentRider]);

  useEffect(() => {
    if (!basicDetails) {
      dispatch(completeStep(false));
      onBack();
    } else {
      if (!isPageLoaded) {
        if (quoteRawResponse) {
          const pocket = new SQSPocketResponse(quoteRawResponse);
          setCalculatedPremium(pocket);
          trackOnload(pocket);
          setIsPageLoaded(true);
        } else {
          if (isEmpty(quotes)) {
            const planPremiumMode = get(initValue, 'planPremiumMode', 'AP');
            const policyTerm = get(initValue, 'policyTerm', '5');
            let nextQuotes = patchValue(planPremiumMode, 'planPremiumMode', quoteInitialSample);
            nextQuotes = patchValue(policyTerm, 'policyTerm', [...nextQuotes]);
            setQuotes([...nextQuotes]);
            console.log(initValue, planPremiumMode, nextQuotes);
            setIsPageLoaded(true);
          }
        }
        const premiumTermsListing = filterPremiumTerms();
        trackOnload(null);
        setPremiumTermOptions(premiumTermsListing);
        setNeedToCalculate(true);
      }

    }
  }, [onBack, setIsPageLoaded, quoteRawResponse, initValue, basicDetails, isPageLoaded, setCalculatedPremium, quotes, dispatch, filterPremiumTerms])

  return (
    <Container>
      <ContainerGrid>
        <FullGrid>
          <MultiSteps steps={5} step={1} label="Choose your coverage... " />
          {isCalculated && (
            <StickyBox><PremiumCost {...premium} /></StickyBox>
          )}
          <Heading margin="0 0 30px"> Choose your coverage </Heading>
        </FullGrid>


        <LeftGrid>
          <form onSubmit={e => { e.preventDefault() }} noValidate>
            <ContainerBlock padding="0" margin="0 0 40px" marginTablet="0 0 60px">
              <Heading variant="sub" fontWeight={600} margin="0 0 15px">DIRECT - Singlife Term Life</Heading>
              <Divider margin="0 0 30px" />
              <Heading variant="label">Sum assured</Heading>
              <Text margin="0 0 15px 0">Please enter an amount between S$50,000 and S$400,000 (in multiples of S$1,000).</Text>

              {!errors?.sumAssured?.isValid && errors?.sumAssured?.message && (<Bullet variant="error">{errors?.sumAssured?.message}</Bullet>)}
              <Input name="sumAssured" type="numeric" onBlur={onBlurBasicSumAssrued} invalid={!isEmpty(errors?.sumAssured?.message)} value={values['sumAssured']} maxLength={9} onChange={onChange} variant="prefix" boxLabel="S$"></Input>

              <Heading variant="label">
                Policy term / Premium payment term* <br />
                * Premium payment term is equal to the policy term
              </Heading>
              <Dropdown name="policyTerm" value={values['policyTerm']} onChange={onChangeDropdown('policyTerm')} source={premiumTermOptions} bindId="code" bindLabel="description" placeholder="Please select" />

              <Heading variant="label">Premium payment frequency</Heading>
              <Dropdown name="planPremiumMode" margin="0" value={values['planPremiumMode']} onChange={onChangeDropdown('planPremiumMode')} source={paymentFrequency} bindId="code" bindLabel="description" placeholder="Please select" />
            </ContainerBlock>
            {
              isShowCI && (
                <React.Fragment>
                  <Heading variant="sub" fontWeight={600} margin="0 0 15px"> Add-on coverage</Heading>
                  <Divider margin="0 0 30px" />
                  <ContainerBlock padding={20} paddingTablet={30} margin="0 0 40px" marginTablet="0 0 60px" background={theme.colors.grey[200]}>
                    <Heading variant="sub" margin="0 0 10px" fontWeight={600}> DIRECT - Singlife Critical Illness (Term Life) (2025) </Heading>
                    <TextCollapsible labelMore="Know more" labelLess="Know less" margin="0 0 30px">
                      <Text margin="0 0 20px">Get a lump-sum payout when you are diagnosed with any of the 30 covered critical illnesses.</Text>
                      <Text margin="0 0 20px">DIRECT - Singlife Critical Illness (Term Life) (2025) does not cover pre-existing medical conditions and claims made in relation to selected critical illnesses within the 90-day waiting period (from policy start date). Should there be a claim for Angioplasty & Other Invasive Treatment For Coronary Artery, 10% of the Sum Assured for this rider, subject to a maximum of S$25,000, is payable.</Text>
                      <Text margin="0 0 10px">The premium rate for the rider is non-guaranteed and may be adjusted by Singapore Life Ltd. based on future claims experience.</Text>
                    </TextCollapsible>
                    <Heading variant="label" margin="0 0 10px">Sum assured</Heading>
                    <Text margin="0 0 15px">Please note: DIRECT - Singlife Critical Illness (Term Life) (2025) rider Sum Assured has to be the same as DIRECT - Singlife Term Life Sum Assured.</Text>

                    {!errors?.illnessCover?.isValid && errors?.illnessCover?.message && (
                      <Bullet variant="error">{errors?.illnessCover?.message}</Bullet>
                    )}
                    <Input margin={0} name="illnessCover" invalid={!isEmpty(errors?.illnessCover?.message)} maxLength={9} type="numeric" value={values['illnessCover']} onChange={onChange} variant="prefix" boxLabel="S$"></Input>
                  </ContainerBlock>
                </React.Fragment>
              )
            }
            <BeforeProcessWarning
              insuranceEstimatorUrl={insuranceEstimatorUrl}
              budgetCalculatorUrl={budgetCalculatorUrl}
              compareFirstUrl={compareFirstUrl}
            />
          </form>

          {needToCalculate ?
            <Button margin="0 0 20px" disabled={!formValid} label="Calculate your premium" onClick={onCalculatePremium} /> :
            <Button margin="0 0 20px" disabled={!formValid} label="Continue" onClick={toggleModal} />
          }
          <Button margin="0 0 40px" variant="secondary" label="Back" onClick={(onBack)} />
        </LeftGrid>
        <EmptyGrid></EmptyGrid>
        <RightGrid>
          <Quote details={quotes} footerNote="*Premium payment term is equal to the policy term" />
          {isCalculated && <PolicyDocument url={productSummary} />}
          <ContactBox items={contactInformation} />
        </RightGrid>

      </ContainerGrid>

      <Modal show={isShowConfirm} onClose={toggleModal}>
        <Heading variant="smallTitle" fontWeight={600} margin="0 0 20px" marginTablet="0 0 30px">Please confirm...</Heading>
        <Heading variant="label" fontWeight="normal" margin="0 0 15px" marginTablet="0 0 10px">Do you meet at least two of the following conditions?</Heading>
        <BulletList margin="0 0 30px">
          <Bullet>You’re below 62 years old.</Bullet>
          <Bullet>You’re proficient in both spoken and written English.</Bullet>
          <Bullet>You’ve attained at least a GCE N-Level or O-Level certification, or equivalent academic qualification.</Bullet>
        </BulletList>
        <Button margin="0 0 20px" label="Yes" onClick={onContinue} />
        <Button variant="secondary" label="No" margin={0} onClick={() => { gotoContact() }} />
      </Modal>
    </Container>
  );
};

export default Coverage;
